import { button } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import { leaveLabel } from "./pause-model.ts";

export interface PauseHandlers {
  /** Fecha a pausa e volta ao jogo (o clique trava o ponteiro de novo). */
  resume(): void;
  /** Abre as Opções ao lado da pausa. */
  settings(): void;
  /** Sai da partida (o fluxo de sair da sala, que volta ao menu). */
  leave(): void;
}

/**
 * Menu de pausa (F80, ui-h à esquerda): "Pausa", o aviso de que o mundo não para (a partida é do servidor,
 * inclusive sozinho) e Continuar, Opções e Sair da partida. Sair pede um segundo clique. Com a pausa
 * aberta, o `body` ganha `pausado` e as Opções abrem ao lado, como no mockup.
 */
export class PauseMenu {
  readonly element: HTMLElement;
  private readonly note: HTMLElement;
  private readonly resumeButton: HTMLButtonElement;
  private readonly settingsButton: HTMLButtonElement;
  private readonly leaveButton: HTMLButtonElement;
  private confirming = false;

  constructor(parent: HTMLElement, handlers: PauseHandlers) {
    this.note = h("p", { class: "pausa-nota", id: "pausa-nota" });
    this.resumeButton = button({ label: "Continuar", icon: "icone-jogar", variant: "primario", onClick: () => handlers.resume() });
    this.resumeButton.id = "pausa-continuar";
    this.settingsButton = button({ label: "Opções", icon: "icone-opcoes", variant: "secundario", onClick: () => handlers.settings() });
    this.settingsButton.id = "pausa-opcoes";
    this.settingsButton.setAttribute("aria-expanded", "false");
    this.leaveButton = button({ label: leaveLabel(false), icon: "icone-sair", variant: "secundario" });
    this.leaveButton.id = "pausa-sair";
    this.leaveButton.addEventListener("click", () => {
      if (!this.confirming) return this.setConfirming(true);
      // Uma saída só: o botão trava até a cena sair (a sala fechando não aceita outra).
      this.leaveButton.disabled = true;
      handlers.leave();
    });
    this.element = h(
      "div",
      { id: "pausa", class: "pausa" },
      h(
        "section",
        { class: "k-painel pausa-painel", role: "dialog", "aria-labelledby": "pausa-titulo", "aria-describedby": "pausa-nota" },
        h("header", { class: "pausa-cabeca" }, h("h2", { id: "pausa-titulo", class: "k-titulo pausa-titulo" }, "Pausa"), h("div", { class: "pausa-ornamento", "aria-hidden": "true" }), this.note),
        h("nav", { class: "pausa-botoes", "aria-label": "Pausa" }, this.resumeButton, this.settingsButton, this.leaveButton),
      ),
    );
    this.element.hidden = true;
    // Tab circula nos três botões enquanto a pausa está aberta sozinha.
    this.element.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;
      const list = [this.resumeButton, this.settingsButton, this.leaveButton];
      const i = list.indexOf(document.activeElement as HTMLButtonElement);
      e.preventDefault();
      list[e.shiftKey ? (i <= 0 ? list.length - 1 : i - 1) : (i + 1) % list.length]!.focus();
    });
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /** Abre com o subtítulo (`pauseNote`) e o foco em Continuar. */
  show(note: string): void {
    this.note.textContent = note;
    this.setConfirming(false);
    this.leaveButton.disabled = false;
    this.element.hidden = false;
    document.body.classList.add("pausado");
    this.focus();
  }

  hide(): void {
    if (this.element.hidden) return;
    this.element.hidden = true;
    this.setConfirming(false);
    this.setSettingsOpen(false);
    document.body.classList.remove("pausado");
  }

  /** Foco na pausa: em Continuar, ou em Opções ao voltar das Opções abertas por ela. */
  focus(target: "continuar" | "opcoes" = "continuar"): void {
    (target === "opcoes" ? this.settingsButton : this.resumeButton).focus();
  }

  /** As Opções abertas ao lado: o botão fica aceso, como o "SETTINGS" do mockup. */
  setSettingsOpen(open: boolean): void {
    this.settingsButton.setAttribute("aria-expanded", String(open));
    this.settingsButton.classList.toggle("k-botao--primario", open);
    this.settingsButton.classList.toggle("k-botao--secundario", !open);
  }

  remove(): void {
    this.hide();
    this.element.remove();
  }

  private setConfirming(on: boolean): void {
    this.confirming = on;
    const label = this.leaveButton.querySelector(".k-rotulo");
    if (label) label.textContent = leaveLabel(on);
    this.leaveButton.classList.toggle("confirmar", on);
  }
}
